<template>
  <layout>
    <page-header>
      <template slot="header">
        <h4 class="mr-4">
          <i class="icon-percent mr-2"></i>
          {{ $t('company.commissions.title') }}
        </h4>
      </template>
    </page-header>
    <div class="content">
      <commissions-notice />
      <b-card v-if="!canGenerateInvoice && unpaidInvoiceId" class="text-danger">
        <h4>
          <i class="icon-exclamation mt-2 mb-2"></i>
          {{ $t('company.commissions.has_unpaid_invoice') }}
          <b-button variant="success" size="lg" @click="goToUnpaid">
            {{ $t('general.pay') }}
          </b-button>
        </h4>
      </b-card>
      <b-card
        v-else-if="!canGenerateInvoice && balance < minBalance"
        class="text-danger"
      >
        <h4>
          <i class="icon-exclamation mt-2 mb-2"></i>
          {{
            $t('company.commissions.no_min_balance', {
              min_balance: `${currency_label}${minBalance}`,
            })
          }}
        </h4>
      </b-card>
      <b-card no-body>
        <div class="card-body">
          <button
            v-if="canGenerateInvoice"
            class="btn btn-primary mr-2"
            @click="generateInvoice"
          >
            Generate invoice
          </button>
          <span class="mr-2">
            {{ $t('company.unprocessed_bookings', { count: totals.count }) }}
          </span>
          <span v-if="isPending">
            Commissions amount:
            <b>{{ totals.commission_amount | currency(currency_label) }}</b>
            / Company amount:
            <b>{{ totals.company_amount | currency(currency_label) }}</b>
            /
            <b class="text-danger">
              Company penalty amount:
              {{ totals.company_penalty_amount | currency(currency_label) }}
            </b>
            / Received by cash:
            <b>{{ totals.cash_payments | currency(currency_label) }}</b>
            /
            <b v-if="balance > 0" class="text-success">
              Company credit: {{ balance | currency(currency_label) }}
            </b>
            <b v-else class="text-danger">
              Company debit: {{ balance | currency(currency_label) }}
            </b>
          </span>
        </div>

        <b-table ref="table" v-bind="tableProps">
          <template slot="bookings" slot-scope="{ item }">
            <span v-for="booking in item.bookings" :key="booking.id">
              <router-link
                :to="{ name: 'bookings.edit', params: { id: booking.id } }"
              >
                #{{ booking.number }}
              </router-link>
              <span class="text-sm text-muted">
                ({{ booking.commission }}%)
              </span>
            </span>
          </template>
          <template slot="bookings_amount" slot-scope="{ item }">
            {{ item.bookings_amount | currency(currency_label) }}

            <div
              v-if="item.bookings_amount < item.bookings_full_price"
              class="text-muted"
            >
              {{ item.bookings_full_price | currency(currency_label) }} ({{
                item.discount
              }}%)
            </div>
          </template>
          <template slot="company_amount" slot-scope="{ item }">
            {{ item.company_amount | currency(currency_label) }}
          </template>
          <template slot="company_penalty_amount" slot-scope="{ item }">
            {{ item.company_penalty_amount | currency(currency_label) }}
          </template>
          <template slot="commission_amount" slot-scope="{ item }">
            {{ item.commission_amount | currency(currency_label) }}
          </template>
          <template slot="stripe_payments" slot-scope="{ item }">
            <span v-if="item.stripe_payments">
              {{ item.stripe_payments | currency(currency_label) }}
            </span>
            <span v-else></span>
          </template>
          <template slot="cash_payments" slot-scope="{ item }">
            <span v-if="item.cash_payments">
              {{ item.cash_payments | currency(currency_label) }}
            </span>
            <span v-else></span>
          </template>
        </b-table>
      </b-card>
    </div>
  </layout>
</template>

<script>
import { AdminTable } from 'stylemix-vue-admin'
import CommissionsApi from './CommissionsApi'
import BookingAmount from '../bookings/BookingAmount'
import CommissionsNotice from './CommissionsNotice'
import ValueDate from '../components/values/ValueDate'
import OrderFinalAmount from '../orders/OrderFinalAmount'

export default {
  name: 'Commissions',

  components: {
    OrderFinalAmount,
    ValueDate,
    CommissionsNotice,
    AdminTable,
    BookingAmount,
  },

  data() {
    return {
      currency: null,
      currency_label: null,
      totals: {},
      total_payments: {},
      unpaidInvoiceId: null,
      minBalance: 100,

      // Table resource configuration
      tableProps: {
        items: () => {
          return new CommissionsApi()
            .setParameters({
              company_id: this.$route.params.id,
            })
            .all()
            .then(result => {
              this.onApiResult(result)
              return result.data
            })
        },
        fields: [
          {
            key: 'bookings',
            label: 'Bookings',
          },
          {
            key: 'bookings_amount',
            label: this.$t('company.commissions.booking_amount'),
          },
          {
            key: 'commission_amount',
            label: this.$t('company.commissions.commission_amount'),
          },
          {
            key: 'company_amount',
            label: this.$t('company.commissions.company_amount'),
            class: 'font-bold',
          },
          {
            key: 'company_penalty_amount',
            label: this.$t('company.commissions.company_penalty_amount'),
            class: 'font-bold',
          },
          {
            key: 'stripe_payments',
            label: 'Online payments',
          },
          {
            key: 'cash_payments',
            label: 'Cash payments',
          },
        ],
      },
    }
  },

  computed: {
    isPending() {
      return this.totals.count > 0
    },
    balance() {
      return (
        this.totals.company_amount -
        this.totals.cash_payments -
        this.totals.company_penalty_amount
      )
    },

    isAdmin() {
      return this.$allows('administer')
    },

    canGenerateInvoice() {
      return (
        this.isAdmin ||
        this.balance < 0 ||
        (this.unpaidInvoiceId === null && this.balance >= this.minBalance)
      )
    },
  },

  methods: {
    onApiResult(result) {
      this.currency = result.currency
      this.currency_label = result.currency_label
      this.unprocessed = result.unprocessed
      this.totals = result.totals
      this.total_payments = result.total_payments
      this.unpaidInvoiceId = result.unpaid_invoice_id
    },
    generateInvoices() {
      this.$uiBlocker(
        this.$http.post('company/process').then(response => {
          this.$toast.success(response.data.message)
          this.unprocessed = response.data.unprocessed
        }),
        this.$refs.table,
      )
    },
    async generateInvoice() {
      const { value } = await this.$alert.question(
        this.$t('company.commissions.request_now_confirm'),
        '',
        {
          showCancelButton: true,
        },
      )

      if (value) {
        new CommissionsApi()
          .process([], this.$route.params.id)
          .then(result => {
            this.$toast.success(result.message)
            this.unprocessed = result.unprocessed
            this.$refs.table.refresh()
          })
          .catch(response => {
            this.$toast.error(response.data.message)
          })
      }
    },

    goToUnpaid() {
      this.$router.push({
        name: 'company-orders-pay',
        params: {
          id: this.unpaidInvoiceId,
        },
      })
    },
  },
}
</script>
